import { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Session Context
 * Manages live session state and real-time updates across the application
 * Uses localStorage for cross-tab communication in demo mode
 */

const SessionContext = createContext(null);

const STORAGE_KEY_PREFIX = 'training_session_';
const EVENT_CHANNEL = 'training_session_events';

export const SessionProvider = ({ children }) => {
  const [activeSessions, setActiveSessions] = useState({});
  const [currentSessionId, setCurrentSessionId] = useState(null);

  // Initialize sessions from localStorage
  useEffect(() => {
    const storedSessions = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const sessionId = key.replace(STORAGE_KEY_PREFIX, '');
          const sessionData = JSON.parse(localStorage.getItem(key));
          storedSessions[sessionId] = sessionData;
        } catch (error) {
          console.error('Failed to parse session data:', error);
        }
      }
    }
    setActiveSessions(storedSessions);
  }, []);

  // Listen for storage events (cross-tab communication)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key?.startsWith(STORAGE_KEY_PREFIX)) {
        const sessionId = e.key.replace(STORAGE_KEY_PREFIX, '');
        if (e.newValue) {
          try {
            const sessionData = JSON.parse(e.newValue);
            setActiveSessions((prev) => ({
              ...prev,
              [sessionId]: sessionData,
            }));
          } catch (error) {
            console.error('Failed to parse session data:', error);
          }
        } else {
          // Session removed
          setActiveSessions((prev) => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Create or join a session
  const joinSession = useCallback((sessionId, participantData) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    let session;
    if (existingSession) {
      session = JSON.parse(existingSession);
      // Add participant if not already in list
      const participantExists = session.participants.some(
        (p) => p.id === participantData.id
      );
      if (!participantExists) {
        session.participants.push({
          ...participantData,
          joinedAt: Date.now(),
        });
        session.participantCount = session.participants.length;
      }
    } else {
      // Create new session
      session = {
        id: sessionId,
        status: 'live',
        createdAt: Date.now(),
        participants: [
          {
            ...participantData,
            joinedAt: Date.now(),
          },
        ],
        participantCount: 1,
        currentActivity: null,
        responses: [],
      };
    }

    localStorage.setItem(sessionKey, JSON.stringify(session));
    setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));
    setCurrentSessionId(sessionId);

    // Emit join event
    emitEvent({
      type: 'participant:joined',
      sessionId,
      data: participantData,
      timestamp: Date.now(),
    });

    return session;
  }, []);

  // Leave a session
  const leaveSession = useCallback((sessionId, participantId) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    if (existingSession) {
      const session = JSON.parse(existingSession);
      session.participants = session.participants.filter(
        (p) => p.id !== participantId
      );
      session.participantCount = session.participants.length;

      if (session.participants.length === 0) {
        // Remove empty session
        localStorage.removeItem(sessionKey);
        setActiveSessions((prev) => {
          const updated = { ...prev };
          delete updated[sessionId];
          return updated;
        });
      } else {
        localStorage.setItem(sessionKey, JSON.stringify(session));
        setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));
      }

      // Emit leave event
      emitEvent({
        type: 'participant:left',
        sessionId,
        data: { participantId },
        timestamp: Date.now(),
      });
    }

    if (currentSessionId === sessionId) {
      setCurrentSessionId(null);
    }
  }, [currentSessionId]);

  // Launch an activity
  const launchActivity = useCallback((sessionId, activity) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    if (existingSession) {
      const session = JSON.parse(existingSession);
      session.currentActivity = {
        ...activity,
        launchedAt: Date.now(),
        responses: [],
      };
      session.responses = [];

      localStorage.setItem(sessionKey, JSON.stringify(session));
      setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));

      // Emit activity launch event
      emitEvent({
        type: 'activity:launched',
        sessionId,
        data: activity,
        timestamp: Date.now(),
      });
    }
  }, []);

  // Close an activity
  const closeActivity = useCallback((sessionId) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    if (existingSession) {
      const session = JSON.parse(existingSession);
      const closedActivity = session.currentActivity;
      session.currentActivity = null;

      localStorage.setItem(sessionKey, JSON.stringify(session));
      setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));

      // Emit activity close event
      emitEvent({
        type: 'activity:closed',
        sessionId,
        data: closedActivity,
        timestamp: Date.now(),
      });
    }
  }, []);

  // Submit a response
  const submitResponse = useCallback((sessionId, participantId, response) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    if (existingSession) {
      const session = JSON.parse(existingSession);

      // Add response
      const newResponse = {
        participantId,
        ...response,
        submittedAt: Date.now(),
      };

      session.responses = session.responses || [];

      // Replace existing response from same participant or add new
      const existingIndex = session.responses.findIndex(
        (r) => r.participantId === participantId
      );

      if (existingIndex >= 0) {
        session.responses[existingIndex] = newResponse;
      } else {
        session.responses.push(newResponse);
      }

      if (session.currentActivity) {
        session.currentActivity.responses = session.responses;
      }

      localStorage.setItem(sessionKey, JSON.stringify(session));
      setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));

      // Emit response event
      emitEvent({
        type: 'response:submitted',
        sessionId,
        data: {
          participantId,
          response: newResponse,
          totalResponses: session.responses.length,
        },
        timestamp: Date.now(),
      });
    }
  }, []);

  // Update session status
  const updateSessionStatus = useCallback((sessionId, status) => {
    const sessionKey = `${STORAGE_KEY_PREFIX}${sessionId}`;
    const existingSession = localStorage.getItem(sessionKey);

    if (existingSession) {
      const session = JSON.parse(existingSession);
      session.status = status;

      localStorage.setItem(sessionKey, JSON.stringify(session));
      setActiveSessions((prev) => ({ ...prev, [sessionId]: session }));

      // Emit status update event
      emitEvent({
        type: 'session:status_changed',
        sessionId,
        data: { status },
        timestamp: Date.now(),
      });
    }
  }, []);

  // Emit an event to all tabs/windows
  const emitEvent = useCallback((event) => {
    // Store event in localStorage temporarily to trigger storage event
    const eventKey = `${EVENT_CHANNEL}_${Date.now()}_${Math.random()}`;
    localStorage.setItem(eventKey, JSON.stringify(event));

    // Clean up old event keys (keep only last 10)
    setTimeout(() => {
      const eventKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(EVENT_CHANNEL)) {
          eventKeys.push(key);
        }
      }

      // Sort by key (timestamp is in key) and remove old ones
      eventKeys.sort().slice(0, -10).forEach((key) => {
        localStorage.removeItem(key);
      });
    }, 100);
  }, []);

  // Get current session
  const getCurrentSession = useCallback(() => {
    if (!currentSessionId) return null;
    return activeSessions[currentSessionId] || null;
  }, [currentSessionId, activeSessions]);

  // Get session by ID
  const getSession = useCallback((sessionId) => {
    return activeSessions[sessionId] || null;
  }, [activeSessions]);

  // Clear all sessions (for cleanup)
  const clearAllSessions = useCallback(() => {
    const sessionKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX) || key?.startsWith(EVENT_CHANNEL)) {
        sessionKeys.push(key);
      }
    }
    sessionKeys.forEach((key) => localStorage.removeItem(key));
    setActiveSessions({});
    setCurrentSessionId(null);
  }, []);

  const value = {
    activeSessions,
    currentSessionId,
    joinSession,
    leaveSession,
    launchActivity,
    closeActivity,
    submitResponse,
    updateSessionStatus,
    getCurrentSession,
    getSession,
    clearAllSessions,
    emitEvent,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

// Custom hook to use session context
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;
